import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Search,
  ChevronRight,
  Mail,
  Phone,
  AlertCircle,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useClientList } from '../api';
import { formatINR } from '@/shared/utils/currency';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { CHANNEL_TIERS, type ClientListFilters } from '../types';

const CHANNEL_LABELS: Record<string, string> = {
  DirectCustomer: 'Direct Customer',
  CustomerFromChannel: 'Channel Customer',
  Reseller: 'Reseller',
  Wholesaler: 'Wholesaler',
  Dealer: 'Dealer',
  Distributor: 'Distributor',
  Stockist: 'Stockist',
  NationalDistributor: 'National Distributor',
};

const CHANNEL_COLORS: Record<string, string> = {
  DirectCustomer: 'bg-info/10 text-info-text dark:bg-info/10/30 dark:text-info-text',
  CustomerFromChannel: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  Reseller: 'bg-green-100 text-success-text dark:bg-green-900/30 dark:text-success-text',
  Wholesaler: 'bg-warning/10 text-warning-text dark:bg-warning/10/30 dark:text-warning-text',
  Dealer: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  Distributor: 'bg-primary-container text-primary dark:bg-primary-container/30 dark:text-primary',
  Stockist: 'bg-primary-container text-primary dark:bg-primary-container/30 dark:text-primary',
  NationalDistributor: 'bg-danger/10 text-danger-text dark:bg-danger/10/30 dark:text-danger-text',
};

export function ClientsListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [hasOutstanding, setHasOutstanding] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Debounce search
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  function handleSearchChange(value: string) {
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 300);
    setSearchTimeout(timeout);
  }

  const filters: ClientListFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      channel: channelFilter || undefined,
      hasOutstanding: hasOutstanding || undefined,
      sortBy,
      sortOrder,
      limit: pageSize,
      skip: page * pageSize,
    }),
    [debouncedSearch, channelFilter, hasOutstanding, sortBy, sortOrder, page],
  );

  const { data, isLoading, isError } = useClientList(filters);
  const clients = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="px-6 py-5 border-b border-border bg-card">
        <PageHeader
          icon={Building2}
          eyebrow="Sales"
          title="Client Profiles"
          description={<>Unified view of all customers, distributors, and channel partners</>}
          actions={
            <>{total > 0 && (
                      <div className="hidden md:flex items-center gap-4">
                        <SummaryPill icon={Users} label="Total" value={total} />
                      </div>
                    )}</>
          }
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-border bg-card/50">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name, email, phone, GST number..."
            className="pl-9 h-9"
          />
        </div>

        <select
          value={channelFilter}
          onChange={(e) => { setChannelFilter(e.target.value); setPage(0); }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Filter by channel tier"
        >
          <option value="">All Channels</option>
          {CHANNEL_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {CHANNEL_LABELS[tier] || tier}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none rounded-md border border-input px-3 h-9 bg-background hover:bg-muted/50 transition-colors">
          <input
            type="checkbox"
            checked={hasOutstanding}
            onChange={(e) => { setHasOutstanding(e.target.checked); setPage(0); }}
            className="rounded border-input accent-primary"
          />
          <AlertTriangle className="w-3.5 h-3.5 text-warning-text" />
          Outstanding &gt; 0
        </label>

        <select
          value={`${sortBy}:${sortOrder}`}
          onChange={(e) => {
            const [sb, so] = e.target.value.split(':') as ['name' | 'createdAt', 'asc' | 'desc'];
            setSortBy(sb);
            setSortOrder(so);
            setPage(0);
          }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Sort order"
        >
          <option value="name:asc">Name A-Z</option>
          <option value="name:desc">Name Z-A</option>
          <option value="createdAt:desc">Newest First</option>
          <option value="createdAt:asc">Oldest First</option>
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {isLoading && (
          <div className="rounded-card border border-border bg-card shadow-xs" aria-busy="true" aria-live="polite">
            <div className="border-b border-border bg-muted px-5 py-3">
              <Skeleton className="h-3 w-24" />
            </div>
            <div className="divide-y divide-border">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <Skeleton className="h-3.5 w-52" />
                  <Skeleton className="ml-auto h-3.5 w-24" />
                </div>
              ))}
            </div>
            <span className="sr-only">Loading clients…</span>
          </div>
        )}

        {isError && (
          <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-card border border-border bg-card p-6 text-center shadow-xs">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
            </div>
            <p className="text-body-md font-medium text-on-surface">Could not load clients</p>
            <p className="text-body-sm text-on-surface-variant">The list did not arrive, so nothing below it is out of date. Reopen this page to load it again.</p>
          </div>
        )}

        {!isLoading && !isError && clients.length === 0 && (
          <div className="flex h-48 flex-col items-center justify-center text-center text-on-surface-variant">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
              <Building2 className="h-8 w-8 text-on-surface-subtle" aria-hidden="true" />
            </div>
            <p className="text-data-lg font-semibold text-on-surface">No clients match this search</p>
            <p className="mt-1 text-body-sm">Clear the search or the channel filter to see the full list.</p>
          </div>
        )}

        {!isLoading && clients.length > 0 && (
          <div className="rounded-card border border-border overflow-hidden bg-card shadow-xs">
            <table className="w-full text-body-sm">
              <thead className="bg-muted">
                <tr className="text-left">
                  <th className="sticky top-0 z-10 bg-muted px-5 py-3 text-label-sm uppercase tracking-wider text-on-surface-variant">Client</th>
                  <th className="sticky top-0 z-10 bg-muted px-4 py-3 text-label-sm uppercase tracking-wider text-on-surface-variant">Contact</th>
                  <th className="sticky top-0 z-10 bg-muted px-4 py-3 text-label-sm uppercase tracking-wider text-on-surface-variant">Channel</th>
                  <th className="sticky top-0 z-10 bg-muted px-4 py-3 text-right text-label-sm uppercase tracking-wider text-on-surface-variant">Credit Limit</th>
                  {hasOutstanding && <th className="sticky top-0 z-10 bg-muted px-4 py-3 text-right text-label-sm uppercase tracking-wider text-on-surface-variant">Outstanding</th>}
                  <th className="sticky top-0 z-10 w-8 bg-muted px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {clients.map((client) => (
                  <tr
                    key={client._id}
                    onClick={() => navigate(`/clients/${client._id}`)}
                    className="cursor-pointer transition-colors hover:bg-muted/50 group"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/clients/${client._id}`); }}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-data-sm font-semibold text-primary">
                            {(client.name || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate">{client.name}</div>
                          {client.gstNumber && (
                            <div className="text-xs text-muted-foreground font-mono">{client.gstNumber}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-0.5">
                        {client.email && (
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Mail className="w-3 h-3 shrink-0" /> <span className="truncate max-w-[180px]">{client.email}</span>
                          </span>
                        )}
                        {client.phone && (
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3 shrink-0" /> {client.phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${CHANNEL_COLORS[client.channelCategory || 'DirectCustomer'] || 'bg-muted text-on-surface-variant'}`}>
                        {CHANNEL_LABELS[client.channelCategory || 'DirectCustomer'] || client.channelCategory}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="font-mono tabular-nums text-data-md">
                        {client.creditLimit ? formatINR(client.creditLimit) : '—'}
                      </span>
                    </td>
                    {hasOutstanding && (
                      <td className="px-4 py-3.5 text-right">
                        <span className={`font-mono tabular-nums text-data-md font-medium ${client.outstanding ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {client.outstanding ? formatINR(client.outstanding) : '—'}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-3.5">
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-card">
          <span className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryPill({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number | string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border">
      <Icon className="w-4 h-4 text-muted-foreground" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}
